import type { VisibilityState } from "@tanstack/react-table";
import { generatePath } from "react-router-dom";

import { PROJECT_DETAILS_PATH_WITH_TAB_ID } from "~/constants/appRoutes.ts";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import {
	type BillingSettingsDivergingFields,
	type BillingSettingsLookupData,
	computeBillingSettingsDifferences,
	type TableLink,
} from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";

export type DivergingOrderBillingSettingsRow = BillingSettingsDivergingFields & {
	orderId: string;
	phaseAndOrderName: TableLink;
};

export function buildDivergingOrderBillingSettingsTableData(
	orders: OrderWithBillingSettings[],
	phases: ProjectPhase[],
	projectBillingSettings: BillingSettings,
	lookups: BillingSettingsLookupData,
	clientId: string,
): DivergingOrderBillingSettingsRow[] {
	const phasesById = new Map(phases.map((p) => [p.id, p]));
	const rows: DivergingOrderBillingSettingsRow[] = [];

	orders
		.filter(
			(order): order is OrderWithBillingSettings & { billingSettings: BillingSettings } =>
				order.billingSettings !== null || !!order.quotaId,
		)
		.forEach((order) => {
			let orderBillingSettings = order.billingSettings

			// this a bit of a hacky workaround to add orders which are assigned to a quota to the table, since they don't have billing settings,
			// but we still want to show them in the table since they always diverge from the project billings settings (for which quotas are not available)
			if(order.quotaId){
				orderBillingSettings = projectBillingSettings;
			}

			const divergingFields = computeBillingSettingsDifferences(orderBillingSettings, projectBillingSettings, lookups, clientId, !!order.quotaId);
			if (!Object.values(divergingFields).some((field) => field.isDifferent)) {
				return;
			}
			const phase = phasesById.get(order.projectPhaseId)!;
			const projectId = phase.projectId;
			const tabId = `phase-${phase.phaseNumber}`;

			rows.push({
				orderId: order.id,
				phaseAndOrderName: {
					displayName: `${getFullPhaseTitle(phase)} - ${order.title}`,
					url: generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, { projectId, tabId }),
				},
				...divergingFields,
			});
		});
	return rows;
}

const DIFFABLE_COLUMNS = [
	"invoiceTypeId",
	"isFixedPrice",
	"isSkillBased",
	"travelExpensesIncluded",
	"isBilledMonthly",
	"paymentTargetDays",
	"currencyDisplayName",
	"resolvedInvoiceRecipient",
	"invoiceByMail",
	"additionalEmailRecipients",
	"billingInstructions",
] as const satisfies readonly (keyof DivergingOrderBillingSettingsRow)[];

export function computeColumnVisibility(rows: DivergingOrderBillingSettingsRow[]): VisibilityState {
	const visibility: VisibilityState = {};

	for (const column of DIFFABLE_COLUMNS) {
		visibility[column] = rows.some((row) => row[column].isDifferent);
	}

	return visibility;
}